import React, {
  ChangeEventHandler,
  FC,
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import Icon from '../../../assets/icons';
import Button from '../../../common/Button';
import TimerClock, { ITimerClock } from '../Clock';
import TimerClockCircle from '../ClockCircle';

import {
  ITimerContainer,
  TimerButtonsContainer,
  TimerClockContainer,
  TimerContainer,
  TimerEditableTitle,
  TimerStopButtonContainer,
  TimerTitle,
  TimerTitleContainer,
  TimerEditableTitleContainer,
} from './styles';

export type BasicTimerPropsType = ITimerContainer &
  Omit<ITimerClock, 'colors'> & {
    title: string;
    defaultTitle: string;
    setPlayerName: (value: string) => void;
    isShowPauseButton?: boolean;
    startGame?: () => void;
    wasPause?: boolean;
    onClickPause?: MouseEventHandler<HTMLButtonElement>;
    onClickStop?: MouseEventHandler<HTMLButtonElement>;
    onClickContinue?: MouseEventHandler<HTMLButtonElement>;
    togglePlaying: any;
  };

const BasicTimer: FC<BasicTimerPropsType> = ({
  isPlaying,
  title,
  onClickPause,
  onClickStop,
  onClickContinue,
  $isActive,
  $active_color,
  $inactive_color,
  setPlayerName,
  wasPause,
  isShowPauseButton,
  startGame,
  togglePlaying,
  defaultTitle,
  $isMainClock = false,
  ...clockProps
}) => {
  const [isEditMode, setEditMode] = useState(false);
  const [name, setName] = useState(
    title !== 'Player 1' && title !== 'Player 2' && title !== 'Team 1' && title !== 'Team 2'
      ? title
      : '',
  );
  const ref = useRef<HTMLInputElement | null>(null);

  const changeEditMode = useCallback(
    (value: boolean) => () => {
      setEditMode(value);
      if (!value) {
        setPlayerName(name);
      }
    },
    [setPlayerName, name],
  );

  useEffect(() => {
    if (isEditMode && ref.current) {
      ref.current?.focus();
    }
  }, [isEditMode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const keyEvent = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        changeEditMode(false)();
      }
    };

    document.addEventListener('keydown', keyEvent);

    return () => {
      document.removeEventListener('keydown', keyEvent);
    };
  }, [changeEditMode]);

  const onChangeHandler: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setName(e.currentTarget.value);
  }, []);

  return (
    <TimerContainer
      $inactive_color={$inactive_color}
      $active_color={$active_color}
      $isActive={$isActive}
    >
      <TimerClockContainer $isActive={$isActive}>
        <TimerTitleContainer>
          <TimerTitle $isActive={$isActive}>{defaultTitle.toUpperCase()}</TimerTitle>
          <TimerEditableTitleContainer>
            <TimerEditableTitle
              ref={ref}
              value={name}
              onChange={onChangeHandler}
              onBlur={changeEditMode(false)}
              onFocus={() => setEditMode(true)}
              $inactive_color={$inactive_color}
              $active_color={$active_color}
              $isActive={$isActive}
              onDoubleClick={changeEditMode(false)}
              placeholder={defaultTitle}
            />
            {isEditMode && <Icon variant="approve" />}
          </TimerEditableTitleContainer>
        </TimerTitleContainer>
        {$isMainClock ? (
          <TimerClock
            {...clockProps}
            $isActive={$isActive}
            isPlaying={isPlaying}
            $isMainClock
            title={defaultTitle}
          />
        ) : (
          <TimerClockCircle
            {...clockProps}
            $isActive={$isActive}
            isPlaying={isPlaying}
            title={defaultTitle}
          />
        )}
      </TimerClockContainer>

      <TimerButtonsContainer>
        <Button
          isActive={startGame ? true : !isPlaying}
          variant="pause"
          onClick={startGame ? startGame : togglePlaying}
          disabled={wasPause}
        />
        <TimerStopButtonContainer>
          <Button
            isActive={startGame ? true : !isPlaying}
            variant="primary"
            onClick={onClickPause}
          >
            <Icon variant="reset" />
            reset time
          </Button>
        </TimerStopButtonContainer>
      </TimerButtonsContainer>
    </TimerContainer>
  );
};

export default BasicTimer;
